import React from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RootState } from '@/redux/store';
import { useToast } from '@/components/ui/use-toast';
import { useGetDashboardConfigQuery, useUpdateDashboardConfigMutation } from '@/services/dashboardConfigApi';
import {
  MembershipConfigFormData,
  membershipConfigSchema,
  defaultMembershipConfigValues,
} from '@/types/dashboard-config';
import ConfigCard from './components/ConfigCard';
import NumberInput from './components/NumberInput';
import { LoadingButton } from '@/components/ui/common/loading-button';
import { ErrorType } from '@/types';

interface MembershipSectionProps {
  refetch: () => void;
}

export default function MembershipSection({ refetch }: MembershipSectionProps) {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { toast } = useToast();

  const [updateConfig, { isLoading }] = useUpdateDashboardConfigMutation();
  const { data: configData } = useGetDashboardConfigQuery({ orgId });

  const form = useForm<MembershipConfigFormData>({
    mode: 'all',
    resolver: zodResolver(membershipConfigSchema),
    defaultValues: defaultMembershipConfigValues,
  });

  const { handleSubmit, formState: { errors }, reset, setValue, watch } = form;
  const [isEditing, setIsEditing] = React.useState(false);

  const resetToLoaded = React.useCallback(() => {
    if (configData?.dashboard_configuration) {
      const config = configData.dashboard_configuration;
      reset({
        membership_new_member_target_daily: config.membership_new_member_target_daily || '',
        membership_new_member_target_weekly: config.membership_new_member_target_weekly || '',
        membership_new_member_target_monthly: config.membership_new_member_target_monthly || '',
        membership_new_member_target_6months: config.membership_new_member_target_6months || '',
        membership_new_member_target_yearly: config.membership_new_member_target_yearly || '',
        membership_churn_rate_threshold: config.membership_churn_rate_threshold || '',
        membership_expiry_window_1: config.membership_expiry_window_1 || '',
        membership_expiry_window_2: config.membership_expiry_window_2 || '',
        membership_expiry_window_3: config.membership_expiry_window_3 || '',
      });
    }
  }, [configData, reset]);

  React.useEffect(() => {
    resetToLoaded();
  }, [resetToLoaded]);

  const handleCancelEdit = React.useCallback(() => {
    setIsEditing(false);
    resetToLoaded();
  }, [resetToLoaded]);

  const onSubmit = async (formData: MembershipConfigFormData) => {
    try {
      const config: Record<string, string> = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          config[key] = String(value);
        }
      });

      await updateConfig({ dashboard_configuration: config }).unwrap();
      toast({ variant: 'success', title: 'Membership parameters saved successfully' });
      setIsEditing(false);
      refetch();
    } catch (error) {
      const typedError = error as ErrorType;
      toast({
        variant: 'destructive',
        title: 'Error',
        description: typedError.data?.detail || typedError.data?.message || 'Failed to update membership parameters',
      });
    }
  };

  const onError = (fieldErrors: any) => {
    console.error('Membership form validation errors:', fieldErrors);
    toast({ variant: 'destructive', description: 'Please check the entered values and fix any validation errors' });
  };

  const columns = [
    { key: 'daily', label: 'Daily' },
    { key: 'weekly', label: 'Weekly' },
    { key: 'monthly', label: 'Monthly' },
    { key: '6months', label: 'Last 6 months' },
    { key: 'yearly', label: 'Yearly' },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} className="flex flex-col gap-4">
      {/* New-member target Matrix */}
      <ConfigCard
        title="New-member target"
        description="Drives the New Members tile and the 6-month trend chart."
      >
        <div className="-mx-1 overflow-x-auto px-1">
          <table className="w-full min-w-[680px] border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="w-40 pb-2 text-left text-[12px] font-medium uppercase tracking-wide text-muted-foreground">
                  Metric
                </th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="pb-2 pl-3 text-right text-[12px] font-medium uppercase tracking-wide text-muted-foreground"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border-t border-border py-2.5 pr-3 align-top text-[13px] text-foreground">
                  <span className="inline-block pt-2">New members</span>
                </td>
                {columns.map((col) => {
                  const fieldKey = `membership_new_member_target_${col.key}` as keyof MembershipConfigFormData;
                  return (
                    <td key={col.key} className="border-t border-border py-2.5 pl-3 align-top">
                      {isEditing ? (
                        <NumberInput
                          aria-label={`New members ${col.label}`}
                          value={String(watch(fieldKey as any) || '')}
                          onChange={(value) => setValue(fieldKey as any, value, { shouldValidate: true, shouldDirty: true })}
                          placeholder="New members target"
                          min={1}
                          error={(errors as any)[fieldKey]?.message}
                        />
                      ) : (
                        <div className="flex min-h-[38px] items-center justify-end rounded-lg border border-border bg-muted/40 px-2.5 py-2 text-[13px] text-foreground">
                          {String(watch(fieldKey as any) || 'Not set')}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </ConfigCard>

      {/* Churn rate threshold */}
      <ConfigCard
        title="Churn rate threshold"
        description="Churn tile badge shows green at or below this rate."
      >
        <div className="max-w-xs">
          {isEditing ? (
            <NumberInput
              aria-label="Churn rate threshold"
              value={String(watch('membership_churn_rate_threshold') || '')}
              onChange={(value) => setValue('membership_churn_rate_threshold', value, { shouldValidate: true, shouldDirty: true })}
              unit="%"
              placeholder="Churn rate threshold"
              min={1}
              max={100}
              error={errors.membership_churn_rate_threshold?.message}
            />
          ) : (
            <div className="flex min-h-[38px] items-center justify-end rounded-lg border border-border bg-muted/40 px-2.5 py-2 text-[13px] text-foreground">
              {String(watch('membership_churn_rate_threshold') || 'Not set')}
            </div>
          )}
        </div>
      </ConfigCard>

      {/* Membership expiry windows */}
      <ConfigCard
        title="Membership expiry windows"
        description="Buckets used on the expiring-memberships list. Each window must be larger than the last."
      >
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <span className="mb-1.5 block text-[13px] font-medium text-foreground">
              Window 1 (days)
            </span>
            {isEditing ? (
              <NumberInput
                aria-label="Window 1 in days"
                value={String(watch('membership_expiry_window_1') || '')}
                onChange={(value) => setValue('membership_expiry_window_1', value, { shouldValidate: true, shouldDirty: true })}
                placeholder="Window 1 in days"
                min={1}
                error={errors.membership_expiry_window_1?.message}
              />
            ) : (
              <div className="flex min-h-[38px] items-center justify-end rounded-lg border border-border bg-muted/40 px-2.5 py-2 text-[13px] text-foreground">
                {String(watch('membership_expiry_window_1') || 'Not set')}
              </div>
            )}
          </div>

          <div>
            <span className="mb-1.5 block text-[13px] font-medium text-foreground">
              Window 2 (days)
            </span>
            {isEditing ? (
              <NumberInput
                aria-label="Window 2 in days"
                value={String(watch('membership_expiry_window_2') || '')}
                onChange={(value) => setValue('membership_expiry_window_2', value, { shouldValidate: true, shouldDirty: true })}
                placeholder="Window 2 in days"
                min={1}
                error={errors.membership_expiry_window_2?.message}
              />
            ) : (
              <div className="flex min-h-[38px] items-center justify-end rounded-lg border border-border bg-muted/40 px-2.5 py-2 text-[13px] text-foreground">
                {String(watch('membership_expiry_window_2') || 'Not set')}
              </div>
            )}
          </div>

          <div>
            <span className="mb-1.5 block text-[13px] font-medium text-foreground">
              Window 3 (days)
            </span>
            {isEditing ? (
              <NumberInput
                aria-label="Window 3 in days"
                value={String(watch('membership_expiry_window_3') || '')}
                onChange={(value) => setValue('membership_expiry_window_3', value, { shouldValidate: true, shouldDirty: true })}
                placeholder="Window 3 in days"
                min={1}
                error={errors.membership_expiry_window_3?.message}
              />
            ) : (
              <div className="flex min-h-[38px] items-center justify-end rounded-lg border border-border bg-muted/40 px-2.5 py-2 text-[13px] text-foreground">
                {String(watch('membership_expiry_window_3') || 'Not set')}
              </div>
            )}
          </div>
        </div>
      </ConfigCard>

      {/* Action buttons */}
      <div className="flex flex-wrap justify-end gap-2 pt-1">
        {!isEditing ? (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            disabled={isLoading}
            className="rounded-lg border border-border bg-card px-3.5 py-2 text-[13px] font-medium text-foreground transition-colors hover:bg-muted/50 disabled:opacity-50"
          >
            Edit
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={handleCancelEdit}
              disabled={isLoading}
              className="rounded-lg border border-border bg-card px-3.5 py-2 text-[13px] font-medium text-foreground transition-colors hover:bg-muted/50 disabled:opacity-50"
            >
              Cancel
            </button>

            <LoadingButton
              type="submit"
              loading={isLoading}
              disabled={isLoading}
              className="rounded-lg px-3.5 py-2 text-[13px] font-medium transition-colors"
            >
              Save changes
            </LoadingButton>
          </>
        )}
      </div>
    </form>
  );
}


